import { FC, useEffect, useMemo, useState } from "react";
import omit from "lodash/omit";
import { useImmer } from "use-immer";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./CopySearchModal.module.scss";
import { withError } from "src/hocs";
import { generateDocId } from "src/store/utils";
import { Form, Input, Label, Switch } from "src/components";
import { useElementFocus, useModal, useTemporaryErrors } from "src/hooks";
import { GRAY, GREEN, SEARCH_INPUT_LIMIT, SEARCH_NAME_INPUT_LIMIT } from "src/constants";
import {
  selectLanguagesByKeywordsDataSources,
  selectLocationsByKeywordsDataSources,
} from "src/store/selectors";
import {
  removeExtraSpaces,
  getDifferenceInObjects,
  getLocationDefaultLanguageId,
} from "src/utils";
import {
  ConfirmModal,
  LocationsDropdown,
  LanguagesDropdown,
} from "src/features";

const InputWithError = withError(Input);

type Props = {
  search: Search.Data;
  duplicateHandler: (value: Search.CreationData, callback?: () => void) => void;
};

export const CopySearchModal: FC<Props> = ({
  search: initialSearch,
  duplicateHandler,
}) => {
  const { t } = useTranslation();

  const [ref, setFocus] = useElementFocus();

  const { closeModal } = useModal();

  const { errors, setErrors } = useTemporaryErrors(3000);

  const defaultSearch = useMemo<Search.CreationData>(
    () =>
      omit({ ...initialSearch, id: generateDocId() }, "createdAt", "updatedAt"),
    [initialSearch],
  );

  const [search, setSearch] = useImmer<Search.CreationData>(defaultSearch);

  const [shouldCopyKeywords, setShouldCopyKeywords] = useState<boolean>(false);

  const locations = useSelector((state: Store.RootState) =>
    selectLocationsByKeywordsDataSources(state, [search.keywordsDataSource]),
  );

  const languages = useSelector((state: Store.RootState) =>
    selectLanguagesByKeywordsDataSources(state, [search.keywordsDataSource]),
  );

  const isSearchChanged = useMemo<boolean>(
    () => Boolean(getDifferenceInObjects(search, defaultSearch)),
    [search, defaultSearch],
  );

  const isDisabled = useMemo<boolean>(
    () => !isSearchChanged,
    [isSearchChanged],
  );

  useEffect(() => setFocus(), [setFocus]);

  const onSubjectChange = (value: string): void =>
    setSearch((draft) => {
      draft.subject = value;
    });

  const onDescriptionChange = (value: string): void =>
    setSearch((draft) => {
      draft.description = value;
    });

  const onLocationIdChange = (value: string): void =>
    setSearch((draft) => {
      if (value === search.locationId) return;

      const languageId = getLocationDefaultLanguageId(
        value,
        locations,
        languages,
      );

      draft.locationId = value;

      draft.languageId = languageId;
    });

  const onLanguageIdChange = (value: string): void =>
    setSearch((draft) => {
      draft.languageId = value;
    });

  const newSearch = useMemo<Search.CreationData>(
    () => {
      if (shouldCopyKeywords) {
        return {
          ...search,
          subject: removeExtraSpaces(search.subject),
          description: removeExtraSpaces(search.description || ""),
          initialSearchId: initialSearch.id,
          isDuplicate: shouldCopyKeywords,
        };
      }

      return {
        ...search,
        subject: removeExtraSpaces(search.subject),
        description: removeExtraSpaces(search.description || ""),
      };
    },
    [search, shouldCopyKeywords, initialSearch.id],
  );

  const onSubmit = (): void => {
    const errors = validate();

    if (Object.keys(errors).length) return setErrors(errors);

    duplicateHandler(
      newSearch,
      () => closeModal("copy-search"),
    );
  };

  function validate() {
    const validationErrors: typeof errors = {};

    const { subject } = search;

    if (!subject.trim())
      validationErrors.name = t(
        "component.modal.duplicate_search.form.validation.name_required",
      );

    return validationErrors;
  }

  const updateShouldCopyKeywords = (): void =>
    setShouldCopyKeywords((state) => !state);

  return (
    <ConfirmModal
      id="copy-search"
      type="info"
      acceptButton={{
        text: t("component.modal.duplicate_search.button.submit"),
        onClick: onSubmit,
        disabled: isDisabled,
      }}
      cancelButton={{
        text: t("component.modal.duplicate_search.button.cancel"),
        onClick: () => closeModal("copy-search"),
      }}
      title={t("component.modal.duplicate_search.title", {
        name: defaultSearch.subject,
      })}
    >
      <Form
        onSubmit={onSubmit}
        disabled={isDisabled}
        className={styles.formWrapper}
      >
        <div className={styles.inputWrapper}>
          <Label leftText={t("component.modal.duplicate_search.form.label.name")} />
          <InputWithError
            ref={ref}
            error={errors.name}
            value={search.subject}
            changeHandler={onSubjectChange}
            characterLimit={SEARCH_NAME_INPUT_LIMIT}
            placeholder={t("component.modal.duplicate_search.form.placeholder.name")}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Label
            leftText={t("component.modal.duplicate_search.form.label.description")}
          />
          <InputWithError
            error={errors.description}
            value={search.description}
            characterLimit={SEARCH_INPUT_LIMIT}
            changeHandler={onDescriptionChange}
            placeholder={t(
              "component.modal.duplicate_search.form.placeholder.description",
            )}
          />
        </div>
        <div className={styles.groupWrapper}>
          <div className={styles.inputWrapper}>
            <Label
              leftText={t("component.modal.duplicate_search.form.label.location")}
            />
            <LocationsDropdown
              locationId={search.locationId}
              setLocationId={onLocationIdChange}
              hasDefaultStyles
              keywordsDataSources={[search.keywordsDataSource]}
            />
          </div>
          <div className={styles.inputWrapper}>
            <Label
              leftText={t("component.modal.duplicate_search.form.label.language")}
            />
            <LanguagesDropdown
              hasDefaultStyles
              languageId={search.languageId}
              setLanguageId={onLanguageIdChange}
              keywordsDataSources={[search.keywordsDataSource]}
            />
          </div>
        </div>
        <div className={styles.keywordsSettings}>
          <Switch
            onColor={GREEN}
            checked={shouldCopyKeywords}
            offColor={GRAY}
            onChange={updateShouldCopyKeywords}
          />
          <span>
            {t("component.modal.duplicate_search.label.should_copy_keywords")}
          </span>
        </div>
      </Form>
    </ConfirmModal>
  );
};
